import React, { useState } from "react";
import { useRegistry } from "../../hooks/useRegistry";
import { useInvitations } from "../../hooks/useInvitations";
import WeddingLayout from "../../components/WeddingLayout";
import AddRegistryItemForm from "../../components/AddRegistryItemForm";
import AddInvitationForm from "../../components/AddInvitationForm";
import RegistryItemsTable from "../../components/RegistryItemsTable";
import InvitationsTable from "../../components/InvitationsTable";
import LoadingSpinner from "../../components/LoadingSpinner";
import { PHOTO_URLS } from "../../constants/wedding";
import { RegistryItem } from "../../lib/supabase";
import { Invitation } from "../../lib/supabase";

const AdminRegistry: React.FC = () => {
  const [showRegistryForm, setShowRegistryForm] = useState(false);
  const [showInvitationForm, setShowInvitationForm] = useState(false);
  const [editingItem, setEditingItem] = useState<RegistryItem | undefined>(
    undefined
  );
  const [editingInvitation, setEditingInvitation] = useState<
    Invitation | undefined
  >(undefined);
  const [editingInvitationId, setEditingInvitationId] = useState<
    string | undefined
  >(undefined);
  const [activeTab, setActiveTab] = useState<"registry" | "invitations">(
    "registry"
  );

  const {
    items,
    loading: registryLoading,
    error: registryError,
    createItem,
    updateItem,
    deleteItem,
  } = useRegistry();
  const {
    invitations,
    loading: invitationsLoading,
    error: invitationsError,
    createInvitation,
    updateInvitation,
    updateInvitationOptimistic,
    deleteInvitation,
  } = useInvitations();

  const handleCreateItem = async (itemData: any) => {
    try {
      await createItem(itemData);
      setShowRegistryForm(false);
    } catch (error) {
      console.error("Failed to create item:", error);
    }
  };

  const handleUpdateItem = async (itemData: any) => {
    if (!editingItem) return;
    try {
      await updateItem(editingItem.id, itemData);
      setShowRegistryForm(false);
      setEditingItem(undefined);
    } catch (error) {
      console.error("Failed to update item:", error);
    }
  };

  const handleEditItem = (item: RegistryItem) => {
    setEditingItem(item);
    setShowRegistryForm(true);
  };

  const handleCancelRegistryForm = () => {
    setShowRegistryForm(false);
    setEditingItem(undefined);
  };

  const handleCreateInvitation = async (invitationData: any) => {
    try {
      await createInvitation(invitationData);
      setShowInvitationForm(false);
    } catch (error) {
      console.error("Failed to create invitation:", error);
    }
  };

  const handleUpdateInvitation = async (invitationData: any) => {
    if (!editingInvitation) return;
    try {
      await updateInvitation(editingInvitation.id, invitationData);
      setShowInvitationForm(false);
      setEditingInvitation(undefined);
    } catch (error) {
      console.error("Failed to update invitation:", error);
    }
  };

  const handleEditInvitation = (invitation: Invitation) => {
    setEditingInvitationId(invitation.id);
  };

  const handleCancelInvitationForm = () => {
    setShowInvitationForm(false);
    setEditingInvitation(undefined);
  };

  const handleUpdateInvitationInline = async (
    invitationId: string,
    invitationData: any
  ) => {
    try {
      await updateInvitation(invitationId, invitationData);
      setEditingInvitationId(undefined);
    } catch (error) {
      console.error("Failed to update invitation:", error);
    }
  };

  const handleCancelEdit = () => {
    setEditingInvitationId(undefined);
  };

  const handleDeleteItem = async (itemId: string) => {
    try {
      await deleteItem(itemId);
    } catch (error) {
      console.error("Failed to delete item:", error);
    }
  };

  const handleDeleteInvitation = async (invitationId: string) => {
    try {
      await deleteInvitation(invitationId);
    } catch (error) {
      console.error("Failed to delete invitation:", error);
    }
  };

  const handleUpdatePrintedCode = async (
    invitationId: string,
    printedCode: boolean
  ) => {
    try {
      await updateInvitationOptimistic(invitationId, {
        printed_code: printedCode,
      });
    } catch (error) {
      console.error("Failed to update printed code:", error);
    }
  };

  const handleUpdateFinalized = async (
    invitationId: string,
    finalized: boolean
  ) => {
    try {
      await updateInvitationOptimistic(invitationId, {
        finalized: finalized,
      });
    } catch (error) {
      console.error("Failed to update finalized status:", error);
    }
  };

  const handleUpdateSent = async (invitationId: string, sent: boolean) => {
    try {
      await updateInvitationOptimistic(invitationId, {
        sent: sent,
      });
    } catch (error) {
      console.error("Failed to update sent status:", error);
    }
  };

  if (registryLoading || invitationsLoading)
    return <LoadingSpinner message="Loading admin data..." />;
  if (registryError || invitationsError) {
    const error = registryError || invitationsError;
    return <div className="error">Error: {error}</div>;
  }

  const renderActivePage = () => {
    switch (activeTab) {
      case "registry":
        return (
          <div className="registry-page">
            <div className="section-header">
              <h2>Registry Items</h2>
              <button
                onClick={() => setShowRegistryForm(!showRegistryForm)}
                className="add-item-btn"
              >
                {showRegistryForm ? "Cancel" : "Add New Item"}
              </button>
            </div>

            {showRegistryForm && (
              <AddRegistryItemForm
                onSubmit={editingItem ? handleUpdateItem : handleCreateItem}
                onCancel={handleCancelRegistryForm}
                item={editingItem}
              />
            )}

            <RegistryItemsTable
              items={items}
              onDelete={handleDeleteItem}
              onEdit={handleEditItem}
            />
          </div>
        );
      case "invitations":
        return (
          <div className="registry-page">
            <div className="section-header">
              <h2>Invitations ({invitations.length})</h2>
              <button
                onClick={() => setShowInvitationForm(!showInvitationForm)}
                className="add-item-btn"
              >
                {showInvitationForm ? "Cancel" : "Add New Invitation"}
              </button>
            </div>

            {showInvitationForm && (
              <AddInvitationForm
                onSubmit={
                  editingInvitation
                    ? handleUpdateInvitation
                    : handleCreateInvitation
                }
                onCancel={handleCancelInvitationForm}
                invitation={editingInvitation}
              />
            )}

            <InvitationsTable
              invitations={invitations}
              onDelete={handleDeleteInvitation}
              onEdit={handleEditInvitation}
              onUpdatePrintedCode={handleUpdatePrintedCode}
              onUpdateFinalized={handleUpdateFinalized}
              onUpdateSent={handleUpdateSent}
              editingInvitationId={editingInvitationId}
              onUpdateInvitation={handleUpdateInvitationInline}
              onCancelEdit={handleCancelEdit}
            />
          </div>
        );
      default:
        return null;
    }
  };

  const adminNavLinks = [
    { id: "registry", label: "Registry Items" },
    { id: "invitations", label: "Invitations" },
  ];

  return (
    <WeddingLayout
      photoUrl={PHOTO_URLS.ADMIN}
      photoAlt="Wedding Admin"
      activeTab={activeTab}
      onTabChange={(tab) => setActiveTab(tab as "registry" | "invitations")}
      customNavLinks={adminNavLinks}
    >
      {renderActivePage()}
    </WeddingLayout>
  );
};

export default AdminRegistry;
